import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { Settings as SettingsIcon, Link2, Bell, Shield, Database, HardDrive, CheckCircle, XCircle, RefreshCw, Unplug, ExternalLink, FileSpreadsheet, Loader2 } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import NinetyNineAcresSync from '../components/NinetyNineAcresSync';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import api from '../lib/api';
import { toast } from 'sonner';
import { useSearchParams } from 'react-router-dom';

const StatRow = ({ label, value }) => (
  <div className="flex justify-between text-sm">
    <span className="text-slate-500">{label}</span>
    <span className="font-medium text-slate-900">{value}</span>
  </div>
);

const InventorySyncCard = () => {
  const [syncing, setSyncing] = useState(false);
  const [lastSync, setLastSync] = useState(null);
  const [syncResult, setSyncResult] = useState(null);
  const [sheetConfig, setSheetConfig] = useState(null);

  useEffect(() => {
    fetchSyncStatus();
    fetchSheetConfig();
  }, []);

  const fetchSyncStatus = async () => {
    try {
      const res = await api.get('/inventory/sync/status');
      const logs = res.data.logs || [];
      if (logs.length > 0) setLastSync(logs[0]);
    } catch { /* ignore */ }
  };

  const fetchSheetConfig = async () => {
    try {
      const res = await api.get('/inventory/sync/sheet-config');
      setSheetConfig(res.data);
    } catch { /* ignore */ }
  };

  const handleSync = async () => {
    setSyncing(true);
    setSyncResult(null);
    try {
      const res = await api.post('/inventory/sync');
      setSyncResult(res.data);
      fetchSyncStatus();
      if (res.data.success) {
        const p = res.data.plots || {};
        const f = res.data.flats || {};
        toast.success(`Sync complete! Plots: +${p.added} ✏️${p.updated} | Flats: +${f.added} ✏️${f.updated}`);
      } else {
        toast.error(res.data.error || 'Sync failed');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Inventory sync failed');
    } finally {
      setSyncing(false);
    }
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : 'Never';

  return (
    <Card className="p-6 border border-slate-200 shadow-[0_2px_8px_rgba(0,0,0,0.04)]" data-testid="inventory-sync-card">
      <div className="flex items-start justify-between mb-5">
        <div className="flex items-center gap-3">
          <div className="p-2.5 bg-emerald-50 rounded-xl">
            <FileSpreadsheet className="w-6 h-6 text-emerald-600" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-slate-900">Google Sheets Inventory</h3>
            <p className="text-sm text-slate-500">Sync Plot & Flat inventory from Google Sheets</p>
          </div>
        </div>
        <div className="text-right">
          <p className="text-[10px] text-slate-400">Last sync</p>
          <p className="text-xs text-slate-600 font-medium">{lastSync ? formatDate(lastSync.timestamp) : 'Never'}</p>
        </div>
      </div>

      <div className="space-y-4">
        <div className="bg-slate-50 rounded-lg p-4 space-y-2">
          <StatRow label="Plot Sheet" value={sheetConfig?.plot_configured ? 'Configured' : 'Not configured'} />
          <StatRow label="Flat Sheet" value={sheetConfig?.flat_configured ? 'Configured' : 'Not configured'} />
        </div>

        <Button className="w-full bg-emerald-600 hover:bg-emerald-700 text-white h-10 text-sm font-semibold" onClick={handleSync} disabled={syncing} data-testid="sync-inventory-btn">
          {syncing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
          {syncing ? 'Syncing Inventory...' : 'Sync Inventory'}
        </Button>

        {syncResult && (
          <div className="bg-slate-50 rounded-lg p-4 space-y-3 text-sm">
            <p className="font-semibold text-slate-900">Sync Results</p>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="font-medium text-slate-700 mb-1">Plots</p>
                <StatRow label="Added" value={syncResult.plots?.added || 0} />
                <StatRow label="Updated" value={syncResult.plots?.updated || 0} />
                <StatRow label="Unchanged" value={syncResult.plots?.unchanged || 0} />
                <StatRow label="Errors" value={syncResult.plots?.errors || 0} />
              </div>
              <div>
                <p className="font-medium text-slate-700 mb-1">Flats</p>
                <StatRow label="Added" value={syncResult.flats?.added || 0} />
                <StatRow label="Updated" value={syncResult.flats?.updated || 0} />
                <StatRow label="Unchanged" value={syncResult.flats?.unchanged || 0} />
                <StatRow label="Errors" value={syncResult.flats?.errors || 0} />
              </div>
            </div>
            {(syncResult.plots?.error_details?.length > 0 || syncResult.flats?.error_details?.length > 0) && (
              <details className="text-xs text-slate-500">
                <summary className="cursor-pointer text-orange-600 font-medium">View errors</summary>
                <div className="mt-1 max-h-32 overflow-y-auto space-y-0.5">
                  {syncResult.plots?.error_details?.map((e, i) => <p key={`p${i}`}>Plot: {e}</p>)}
                  {syncResult.flats?.error_details?.map((e, i) => <p key={`f${i}`}>Flat: {e}</p>)}
                </div>
              </details>
            )}
          </div>
        )}
      </div>
    </Card>
  );
};

const Settings = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('integrations');
  const [driveStatus, setDriveStatus] = useState(null);
  const [driveTesting, setDriveTesting] = useState(false);
  const [searchParams] = useSearchParams();

  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';
  const isSuperAdmin = user?.role === 'super_admin';

  useEffect(() => {
    if (isAdmin) fetchDriveStatus();
    if (searchParams.get('drive_connected') === 'true') {
      toast.success('Google Drive connected successfully!');
      fetchDriveStatus();
    }
    if (searchParams.get('drive_error')) {
      toast.error(`Drive connection failed: ${searchParams.get('drive_error')}`);
    }
  }, []); // eslint-disable-line

  const fetchDriveStatus = async () => {
    try {
      const res = await api.get('/drive/status');
      setDriveStatus(res.data);
    } catch { setDriveStatus({ connected: false }); }
  };

  const handleConnectDrive = async () => {
    try {
      const res = await api.get('/drive/connect');
      window.location.href = res.data.authorization_url;
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to initiate connection'); }
  };

  const handleTestDrive = async () => {
    setDriveTesting(true);
    try {
      const res = await api.post('/drive/test');
      toast.success(`Connected to ${res.data.email}`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Connection test failed'); }
    setDriveTesting(false);
  };

  const handleDisconnectDrive = async () => {
    if (!window.confirm('Are you sure you want to disconnect Google Drive? Existing document references will be preserved.')) return;
    try {
      await api.post('/drive/disconnect');
      toast.success('Google Drive disconnected');
      setDriveStatus({ connected: false });
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : 'N/A';

  return (
    <Layout>
      <div className="aavaas-page">
        <div className="mb-8">
          <h1 className="aavaas-page-title" data-testid="settings-title">Settings</h1>
          <p className="aavaas-page-subtitle">Manage your account and integrations</p>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-slate-100 p-1">
            {isAdmin && (
              <TabsTrigger value="integrations" className="data-[state=active]:bg-white">
                <Link2 className="w-4 h-4 mr-2" />
                Integrations
              </TabsTrigger>
            )}
            <TabsTrigger value="notifications" className="data-[state=active]:bg-white">
              <Bell className="w-4 h-4 mr-2" />
              Notifications
            </TabsTrigger>
            <TabsTrigger value="account" className="data-[state=active]:bg-white">
              <Shield className="w-4 h-4 mr-2" />
              Account
            </TabsTrigger>
          </TabsList>

          {isAdmin && (
            <TabsContent value="integrations" className="space-y-6">
              {/* Google Drive Integration */}
              <Card className="p-6 border border-slate-200 shadow-[0_2px_8px_rgba(0,0,0,0.04)]" data-testid="drive-integration">
                <div className="flex items-start justify-between mb-5">
                  <div className="flex items-center gap-3">
                    <div className="p-2.5 bg-blue-50 rounded-xl">
                      <HardDrive className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-slate-900">Google Drive</h3>
                      <p className="text-sm text-slate-500">Document storage for leads, clients, deals & projects</p>
                    </div>
                  </div>
                  {driveStatus?.connected ? (
                    <span className="flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
                      <CheckCircle className="w-3.5 h-3.5" /> Connected
                    </span>
                  ) : (
                    <span className="flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-slate-100 text-slate-500 border border-slate-200">
                      <XCircle className="w-3.5 h-3.5" /> Not Connected
                    </span>
                  )}
                </div>

                {driveStatus?.connected ? (
                  <div className="space-y-4">
                    <div className="bg-slate-50 rounded-lg p-4 space-y-2">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-slate-500">Connected Account</span>
                        <span className="font-medium text-slate-900">{driveStatus.connected_email}</span>
                      </div>
                      {driveStatus.connected_name && (
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-slate-500">Account Name</span>
                          <span className="font-medium text-slate-900">{driveStatus.connected_name}</span>
                        </div>
                      )}
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-slate-500">Connected On</span>
                        <span className="font-medium text-slate-900">{formatDate(driveStatus.connected_at)}</span>
                      </div>
                      {driveStatus.root_folder_id && (
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-slate-500">Root Folder</span>
                          <a href={`https://drive.google.com/drive/folders/${driveStatus.root_folder_id}`} target="_blank" rel="noreferrer" className="text-blue-600 font-medium flex items-center gap-1 hover:underline">
                            Aavaas CRM <ExternalLink className="w-3 h-3" />
                          </a>
                        </div>
                      )}
                    </div>
                    {isSuperAdmin && (
                      <div className="flex gap-2">
                        <Button variant="outline" className="text-sm h-9" onClick={handleTestDrive} disabled={driveTesting} data-testid="test-drive-btn">
                          <RefreshCw className={`w-4 h-4 mr-1.5 ${driveTesting ? 'animate-spin' : ''}`} /> Test Connection
                        </Button>
                        <Button variant="outline" className="text-sm h-9 text-red-600 border-red-200 hover:bg-red-50" onClick={handleDisconnectDrive} data-testid="disconnect-drive-btn">
                          <Unplug className="w-4 h-4 mr-1.5" /> Disconnect
                        </Button>
                      </div>
                    )}
                  </div>
                ) : (
                  <div>
                    {isSuperAdmin ? (
                      <Button className="bg-blue-600 hover:bg-blue-700 text-white h-10 text-sm font-semibold" onClick={handleConnectDrive} data-testid="connect-drive-btn">
                        <HardDrive className="w-4 h-4 mr-2" /> Connect Google Drive
                      </Button>
                    ) : (
                      <p className="text-sm text-slate-500">Contact your Super Admin to connect Google Drive.</p>
                    )}
                  </div>
                )}
              </Card>

              {/* Google Sheets Inventory Sync */}
              {driveStatus?.connected && (
                <InventorySyncCard />
              )}

              {/* Lead Source Integrations */}
              <div>
                <h2 className="text-lg font-semibold text-slate-900 mb-4">Lead Source Integrations</h2>
                <div className="space-y-4">
                  <NinetyNineAcresSync />

                  <Card className="p-6">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-purple-100 rounded-lg">
                          <Database className="w-6 h-6 text-purple-600" />
                        </div>
                        <div>
                          <h3 className="text-lg font-semibold text-slate-900">Property Listing Sync</h3>
                          <p className="text-sm text-slate-600">Post your properties to 99acres and MagicBricks</p>
                        </div>
                      </div>
                      <span className="px-3 py-1 text-xs font-medium bg-yellow-100 text-yellow-700 rounded-full">API Access Required</span>
                    </div>
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-sm">
                      <p className="text-yellow-800 font-medium mb-2">Action Required:</p>
                      <p className="text-yellow-700">Contact 99acres support to enable API access. Your current credentials are for website login only.</p>
                      <p className="text-yellow-700 mt-2"><strong>Email:</strong> support@99acres.com<br/><strong>Account:</strong> Aavaas99</p>
                    </div>
                  </Card>

                  <Card className="p-6">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-orange-100 rounded-lg"><Database className="w-6 h-6 text-orange-600" /></div>
                        <div>
                          <h3 className="text-lg font-semibold text-slate-900">MagicBricks</h3>
                          <p className="text-sm text-slate-600">Connect to MagicBricks to sync property leads</p>
                        </div>
                      </div>
                      <span className="px-3 py-1 text-xs font-medium bg-slate-100 text-slate-600 rounded-full">Coming Soon</span>
                    </div>
                  </Card>
                </div>
              </div>
            </TabsContent>
          )}

          <TabsContent value="notifications" className="space-y-6">
            <Card className="p-6">
              <h2 className="text-lg font-semibold text-slate-900 mb-4">Notification Preferences</h2>
              <p className="text-slate-600 text-sm">Configure how you receive notifications</p>
              <div className="mt-6 text-slate-400 text-sm">Notification settings coming soon...</div>
            </Card>
          </TabsContent>

          <TabsContent value="account" className="space-y-6">
            <Card className="p-6">
              <h2 className="text-lg font-semibold text-slate-900 mb-4">Account Settings</h2>
              <div className="space-y-4">
                <div><label className="text-sm font-medium text-slate-500">Name</label><p className="text-slate-900">{user?.name}</p></div>
                <div><label className="text-sm font-medium text-slate-500">Email</label><p className="text-slate-900">{user?.email}</p></div>
                <div><label className="text-sm font-medium text-slate-500">Phone</label><p className="text-slate-900">{user?.phone}</p></div>
                <div><label className="text-sm font-medium text-slate-500">Role</label><p className="text-slate-900 capitalize">{user?.role?.replace('_', ' ')}</p></div>
              </div>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
};

export default Settings;
